/**
 * Members Table Component
 * Displays overview of all members/users
 */

import React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Text,
  Button,
  Box,
} from "@chakra-ui/react";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import CardHeader from "../../../../components/Card/CardHeader";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

export interface Member {
  id: string;
  email: string;
  name: string;
  status: "active" | "inactive";
  subscription: "active" | "cancelled" | "trial";
  portfolioSize: number;
  totalPnL: number;
  totalTrades: number;
  lastActive: string; // ISO date string
  joinedDate: string; // ISO date string
}

interface MembersTableProps {
  members: Member[];
  onViewStats: (member: Member) => void;
}

const MembersTable: React.FC<MembersTableProps> = ({ members, onViewStats }) => {
  const { formatMoney } = useDisplayCurrency();
  const textColor = useColorModeValue("#575757", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const secondaryTextColor = useColorModeValue("gray.400", "gray.400");
  const cardBg = useColorModeValue("white", "#151517");

  // Format date
  const formatDate = (date: string): string => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  // Format relative time
  const formatRelativeTime = (date: string): string => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getStatusBadge = (status: Member["status"]) => {
    if (status === "active") {
      return (
        <Badge
          bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
          color="white"
          fontSize="xs"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontWeight="medium"
          textTransform="uppercase"
          p="3px 10px"
          borderRadius="8px"
        >
          Active
        </Badge>
      );
    }
    return (
      <Badge
        bg="gray.500"
        color="white"
        fontSize="xs"
        fontFamily="'IBM Plex Sans', sans-serif"
        fontWeight="medium"
        textTransform="uppercase"
        p="3px 10px"
        borderRadius="8px"
      >
        Inactive
      </Badge>
    );
  };

  const getSubscriptionBadge = (subscription: Member["subscription"]) => {
    if (subscription === "active") {
      return (
        <Badge
          bgGradient="linear-gradient(135deg, #06E363, #AAFAB1)"
          color="white"
          fontSize="xs"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontWeight="medium"
          textTransform="uppercase"
          p="3px 10px"
          borderRadius="8px"
        >
          Active
        </Badge>
      );
    }
    if (subscription === "trial") {
      return (
        <Badge
          bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
          color="white"
          fontSize="xs"
          fontFamily="'IBM Plex Sans', sans-serif"
          fontWeight="medium"
          textTransform="uppercase"
          p="3px 10px"
          borderRadius="8px"
        >
          Trial
        </Badge>
      );
    }
    return (
      <Badge
        bg="gray.500"
        color="white"
        fontSize="xs"
        fontFamily="'IBM Plex Sans', sans-serif"
        fontWeight="medium"
        textTransform="uppercase"
        p="3px 10px"
        borderRadius="8px"
      >
        Cancelled
      </Badge>
    );
  };

  return (
    <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
      <CardHeader pb={4}>
        <Text
          fontSize="sm"
          color={secondaryTextColor}
          fontWeight="medium"
          fontFamily="'IBM Plex Sans', sans-serif"
          textTransform="uppercase"
        >
          Members Overview
        </Text>
      </CardHeader>
      <CardBody p={0}>
        <Box w="100%" overflowX="auto">
          <Table variant="simple" w="100%">
            <Thead bg={cardBg} borderTopRadius="md">
              <Tr>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                >
                  User
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                >
                  Name
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                >
                  Status
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                >
                  Subscription
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                  isNumeric
                >
                  Portfolio Size
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                  isNumeric
                >
                  Total P&L
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                  isNumeric
                >
                  Total Trades
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                >
                  Last Active
                </Th>
                <Th
                  color={secondaryTextColor}
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight="medium"
                  fontSize="xs"
                  textTransform="uppercase"
                  borderColor={borderColor}
                >
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {members.map((member) => {
                const isProfit = member.totalPnL > 0;
                const isLoss = member.totalPnL < 0;

                return (
                  <Tr
                    key={member.id}
                    _hover={{ bg: rowHoverBg }}
                    transition="background-color 0.2s"
                  >
                    <Td borderColor={borderColor}>
                      <Text
                        fontSize="sm"
                        color={textColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                      >
                        {member.email}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text
                        fontSize="sm"
                        color={textColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                      >
                        {member.name}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>{getStatusBadge(member.status)}</Td>
                    <Td borderColor={borderColor}>
                      {getSubscriptionBadge(member.subscription)}
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      <Text
                        fontSize="sm"
                        color={textColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                      >
                        {formatMoney(member.portfolioSize)}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      <Text
                        fontSize="sm"
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                        bgGradient={
                          isProfit
                            ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                            : isLoss
                            ? "linear-gradient(135deg, #E00101, #FE4244)"
                            : "gray.500"
                        }
                        bgClip="text"
                        css={{
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {formatMoney(member.totalPnL)}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor} isNumeric>
                      <Text
                        fontSize="sm"
                        color={textColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                      >
                        {member.totalTrades}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text
                        fontSize="sm"
                        color={secondaryTextColor}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                      >
                        {formatRelativeTime(member.lastActive)}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewStats(member)}
                        fontFamily="'IBM Plex Sans', sans-serif"
                        fontWeight="medium"
                        fontSize="xs"
                        textTransform="uppercase"
                        borderColor={borderColor}
                        color={textColor}
                        _hover={{ bg: rowHoverBg }}
                      >
                        View Stats
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </CardBody>
    </Card>
  );
};

export default MembersTable;

